import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import {
  FiPlus,
  FiImage,
  FiEdit2,
  FiTrash2,
  FiX,
  FiCheck,
} from "react-icons/fi";
import { motion } from "framer-motion";
import StatusConfirmationModal from "../../../components/StatusConfirmationModal";
import api from "../../../../api/api";

const AboutContentModal = ({ isOpen, onClose, contentToEdit, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    place: "",
    date: "",
    description: "",
  });

  useEffect(() => {
    if (contentToEdit) {
      setFormData({
        name: contentToEdit.name || "",
        place: contentToEdit.place || "",
        date: contentToEdit.date || "",
        description: contentToEdit.description || "",
      });
    }
  }, [contentToEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.name ||
      !formData.place ||
      !formData.date ||
      !formData.description
    ) {
      toast.error("Please fill all required fields");
      return;
    }
    await onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold">
            {contentToEdit ? "Edit About Content" : "Add About Content"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name*</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full border rounded-lg p-2"
                placeholder="Enter company name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Place*</label>
              <input
                type="text"
                value={formData.place}
                onChange={(e) =>
                  setFormData({ ...formData, place: e.target.value })
                }
                className="w-full border rounded-lg p-2"
                placeholder="Enter location"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Date*</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
              className="w-full border rounded-lg p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Description*
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={6}
              className="w-full border rounded-lg p-2"
              placeholder="Enter company description"
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
            >
              <FiCheck className="w-4 h-4" />
              {contentToEdit ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AboutContentCard = ({ content, onEdit, onDelete }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">
            {content.name}
          </h3>
          <p className="text-gray-600">{content.place}</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(content)}
            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
          >
            <FiEdit2 size={18} />
          </button>
          <button
            onClick={() => onDelete(content)}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
          >
            <FiTrash2 size={18} />
          </button>
        </div>
      </div>
      <div className="space-y-3">
        <div>
          <span className="text-sm font-medium text-gray-500">
            Established Date:
          </span>
          <span className="ml-2 text-gray-700">
            {new Date(content.date).toLocaleDateString()}
          </span>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-500">
            Description:
          </span>
          <p className="mt-1 text-gray-700">{content.description}</p>
        </div>
      </div>
    </motion.div>
  );
};

const AboutTab = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isEditingAboutImages, setIsEditingAboutImages] = useState(false);
  const [aboutPageImages, setAboutPageImages] = useState({
    bannerImage: "",
    subImage1: "",
    subImage2: "",
  });
  const [aboutContents, setAboutContents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [contentToEdit, setContentToEdit] = useState(null);
  // Add states for delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contentToDelete, setContentToDelete] = useState(null);

  useEffect(() => {
    Promise.all([fetchAboutImages(), fetchAboutContents()]).finally(() =>
      setIsLoading(false)
    );
  }, []);

  const fetchAboutContents = async () => {
    try {
      const response = await api.getAboutContent();
      setAboutContents(response || []);
    } catch (error) {
      toast.error("Failed to fetch about contents");
      console.error("Error fetching about contents:", error);
    }
  };

  const handleSaveContent = async (formData) => {
    try {
      if (contentToEdit) {
        await api.updateAboutContent(contentToEdit.id, formData);
        toast.success("Content updated successfully");
      } else {
        await api.createAboutContent(formData);
        toast.success("Content added successfully");
      }
      await fetchAboutContents();
    } catch (error) {
      toast.error(
        contentToEdit ? "Failed to update content" : "Failed to add content"
      );
      console.error("Error saving content:", error);
    }
  };

  const handleDeleteClick = (content) => {
    setContentToDelete(content);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await api.deleteAboutContent(contentToDelete.id);
      await fetchAboutContents();
      toast.success("Content deleted successfully");
    } catch (error) {
      toast.error("Failed to delete content");
      console.error("Error deleting content:", error);
    } finally {
      setShowDeleteModal(false);
      setContentToDelete(null);
    }
  };

  const handleEdit = (content) => {
    setContentToEdit(content);
    setShowModal(true);
  };

  const fetchAboutImages = async () => {
    try {
      setIsLoading(true);
      const response = await api.getAboutImages();
      setAboutPageImages({
        bannerImage: response.banner_image || "",
        subImage1: response.sub_image_1 || "",
        subImage2: response.sub_image_2 || "",
      });
    } catch (error) {
      toast.error("Failed to fetch about page images");
      console.error("Error fetching about images:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = async (type, file) => {
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setAboutPageImages((prev) => ({
          ...prev,
          [type]: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveImages = async () => {
    try {
      const formData = new FormData();

      // Convert either a data URL or an existing remote URL to a Blob
      const toBlob = async (source, fallbackName) => {
        if (!source) return null;

        // When a new image is selected we store it as a data URL
        if (source.startsWith("data:")) {
          const response = await fetch(source);
          const blob = await response.blob();
          const name = fallbackName.includes(".")
            ? fallbackName
            : `${fallbackName}.jpg`;
          return { blob, name };
        }

        // When no change is made we still need to send the current image
        try {
          const response = await fetch(source, { mode: "cors" });
          if (!response.ok) return null;
          const blob = await response.blob();
          const contentType =
            response.headers.get("content-type") || "image/jpeg";
          const extension = contentType.split("/")[1] || "jpg";
          const name = fallbackName.includes(".")
            ? fallbackName
            : `${fallbackName}.${extension}`;
          return { blob, name };
        } catch (err) {
          console.error("Failed to fetch existing image blob:", err);
          return null;
        }
      };

      // Prepare all three images (required by backend)
      const [bannerFile, sub1File, sub2File] = await Promise.all([
        toBlob(aboutPageImages.bannerImage, "banner"),
        toBlob(aboutPageImages.subImage1, "sub1"),
        toBlob(aboutPageImages.subImage2, "sub2"),
      ]);

      // If any image is missing, abort to avoid 400 from required fields
      if (!bannerFile || !sub1File || !sub2File) {
        toast.error(
          "All three images are required. Please add missing images."
        );
        return;
      }

      formData.append("banner_image", bannerFile.blob, bannerFile.name);
      formData.append("sub_image_1", sub1File.blob, sub1File.name);
      formData.append("sub_image_2", sub2File.blob, sub2File.name);

      await api.updateAboutImages(formData);
      setIsEditingAboutImages(false);
      toast.success("About page images updated successfully");
      await fetchAboutImages(); // Refresh the images
    } catch (error) {
      toast.error("Failed to update about page images");
      console.error("Error updating about images:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Images Section */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold">About Page Images</h3>
          <button
            onClick={() => setIsEditingAboutImages(!isEditingAboutImages)}
            className="text-indigo-600 hover:text-indigo-700"
          >
            {isEditingAboutImages ? <FiX size={24} /> : <FiEdit2 size={20} />}
          </button>
        </div>

        <div className="space-y-6">
          {/* Banner Image Section */}
          <div className="space-y-4">
            <h4 className="font-medium">Banner Image</h4>
            <div className="border rounded-lg p-4 bg-gray-50">
              {aboutPageImages.bannerImage ? (
                <img
                  src={aboutPageImages.bannerImage}
                  alt="Banner"
                  className="w-full h-48 object-cover rounded-lg"
                />
              ) : (
                <div className="flex items-center justify-center h-48 bg-gray-100 rounded-lg">
                  <FiImage className="w-8 h-8 text-gray-400" />
                </div>
              )}
            </div>
            {isEditingAboutImages && (
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageChange("bannerImage", e.target.files[0])
                }
                className="w-full border rounded-lg p-2"
              />
            )}
          </div>

          {/* Sub Images Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sub Image 1 */}
            <div className="space-y-4">
              <h4 className="font-medium">Sub Image 1</h4>
              <div className="border rounded-lg p-4 bg-gray-50">
                {aboutPageImages.subImage1 ? (
                  <img
                    src={aboutPageImages.subImage1}
                    alt="Sub Image 1"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                ) : (
                  <div className="flex items-center justify-center h-40 bg-gray-100 rounded-lg">
                    <FiImage className="w-8 h-8 text-gray-400" />
                  </div>
                )}
              </div>
              {isEditingAboutImages && (
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleImageChange("subImage1", e.target.files[0])
                  }
                  className="w-full border rounded-lg p-2"
                />
              )}
            </div>

            {/* Sub Image 2 */}
            <div className="space-y-4">
              <h4 className="font-medium">Sub Image 2</h4>
              <div className="border rounded-lg p-4 bg-gray-50">
                {aboutPageImages.subImage2 ? (
                  <img
                    src={aboutPageImages.subImage2}
                    alt="Sub Image 2"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                ) : (
                  <div className="flex items-center justify-center h-40 bg-gray-100 rounded-lg">
                    <FiImage className="w-8 h-8 text-gray-400" />
                  </div>
                )}
              </div>
              {isEditingAboutImages && (
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleImageChange("subImage2", e.target.files[0])
                  }
                  className="w-full border rounded-lg p-2"
                />
              )}
            </div>
          </div>

          {isEditingAboutImages && (
            <div className="flex justify-end space-x-2 pt-4">
              <button
                onClick={() => setIsEditingAboutImages(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveImages}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <FiCheck className="w-4 h-4" />
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>

      {/* About Content Section */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold">About Page Contents</h3>
          <button
            onClick={() => {
              setContentToEdit(null);
              setShowModal(true);
            }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
          >
            <FiPlus className="h-5 w-5" />
            Add Content
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {aboutContents.map((content) => (
            <AboutContentCard
              key={content.id}
              content={content}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
            />
          ))}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AboutContentModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setContentToEdit(null);
        }}
        contentToEdit={contentToEdit}
        onSave={handleSaveContent}
      />

      {/* Delete Confirmation Modal */}
      <StatusConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setContentToDelete(null);
        }}
        onConfirm={confirmDelete}
        itemName="Content"
        currentStatus={true}
      />
    </div>
  );
};

export default AboutTab;
